import React, { useEffect, useState } from 'react';
import { useGetOne, useRecordContext } from 'react-admin';
import { Warehouse } from 'lucide-react';

import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Funeral } from '../types';
import { fetchOnFileTypes } from '../components/dispatch/dispatchDocuments';
import { PARTNER_DOCS_CHANGED } from '../components/dispatch/partnerDocsApi';
import {
  DROPOFF_STATUS_LABEL,
  DropoffStatus,
  MCCD_STATUS_LABEL,
  MccdStatus,
  daysInCare,
  dropoffReleaseTrigger,
  dropoffStatus,
  mccdMissingAfterArrival,
  mccdStatus,
  pacemakerLabel,
  shortDateTime,
  shortDay,
} from './dropoffLedger';
import { BelongingsEdit } from './BelongingsEdit';
import { BelongingsShipping } from './BelongingsShipping';

const STATUS_TONE: Record<DropoffStatus, string> = {
  on_the_way: 'bg-muted text-muted-foreground',
  in_care: 'bg-purple-100 text-purple-900',
  released: 'bg-emerald-100 text-emerald-900',
  completed: 'bg-muted text-muted-foreground',
};

const MCCD_TONE: Partial<Record<MccdStatus, string>> = {
  on_file: 'text-emerald-700',
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-3 py-1.5">
      <span className="text-[13px] text-muted-foreground leading-5 shrink-0">{label}</span>
      <div className="text-[13px] text-foreground leading-5 min-w-0 text-right">{children}</div>
    </div>
  );
}

/** Whether an active medical_certificate is on file; refreshes when partner docs change. */
function useMccdOnFile(funeralId: Funeral['id'] | undefined): boolean | null {
  const [onFile, setOnFile] = useState<boolean | null>(null);
  useEffect(() => {
    if (!funeralId) return;
    let cancelled = false;
    const load = () => {
      void fetchOnFileTypes(funeralId, ['medical_certificate'])
        .then((d) => {
          if (!cancelled) setOnFile(!!d.medical_certificate);
        })
        .catch(() => {
          if (!cancelled) setOnFile(null);
        });
    };
    load();
    const onChange = (e: Event) => {
      if (String((e as CustomEvent).detail?.funeralId) === String(funeralId)) load();
    };
    window.addEventListener(PARTNER_DOCS_CHANGED, onChange);
    return () => {
      cancelled = true;
      window.removeEventListener(PARTNER_DOCS_CHANGED, onChange);
    };
  }, [funeralId]);
  return onFile;
}

/**
 * The drop-off facility's ledger entries for this case (DROPOFF_LEDGER_PLAN
 * §6 "ciel admin"): read-only here — the facility records them in the partner
 * portal — except what belongings came in (T-143) and how they go home (T-099). Renders nothing unless the case has a drop-off location.
 */
export function DropoffSidebarSection() {
  const record = useRecordContext<Funeral>();
  const partnerId = record?.dropoff_partner_id;
  const { data: partner } = useGetOne(
    'partners',
    { id: partnerId as string },
    { enabled: !!partnerId },
  );
  const onFile = useMccdOnFile(partnerId ? record?.id : undefined);

  if (!record || !partnerId) return null;

  const status = dropoffStatus(record);
  const trigger = dropoffReleaseTrigger(record);
  const stay = daysInCare(record);
  const mccd = onFile === null ? null : mccdStatus(onFile);
  // T-051: who sent it doesn't matter, only whether one is on file. The one
  // nudge left is none on file once the body has arrived.
  const mccdMissing = onFile !== null && mccdMissingAfterArrival(record, onFile);
  const storageDays = record.dropoff_storage_days ?? 0;

  return (
    <AccordionItem value="dropoff">
      <AccordionTrigger icon={<Warehouse className="w-4 h-4" />}>
        <span className="flex items-center gap-2">
          Drop-off
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[status]}`}
          >
            {DROPOFF_STATUS_LABEL[status]}
            {status === 'in_care' && stay ? ` · Day ${stay.day}` : ''}
          </span>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-0.5">
          <Row label="Facility">
            <span>{partner?.name ?? '…'}</span>
          </Row>
          <Row label="Status">
            <div>
              <div>{DROPOFF_STATUS_LABEL[status]}</div>
              {trigger && (
                <div className="text-xs text-muted-foreground">
                  {trigger === 'facility' && record.dropoff_released_at
                    ? `Facility released ${shortDay(record.dropoff_released_at)}${
                        record.dropoff_released_by ? ` · ${record.dropoff_released_by}` : ''
                      }`
                    : 'Cleo scheduled the cremation'}
                </div>
              )}
            </div>
          </Row>
          <Row label="Arrived">
            {record.dropoff_received_at ? (
              <span>
                {shortDateTime(record.dropoff_received_at)}
                {record.dropoff_received_by ? ` · ${record.dropoff_received_by}` : ''}
              </span>
            ) : (
              <span className="text-muted-foreground text-xs">Not recorded</span>
            )}
          </Row>
          {stay && (
            <Row label={stay.ended ? 'Time in care' : 'In care'}>
              <span>
                Day {stay.day}
                <span className="text-muted-foreground"> · ≈ {stay.hours} h</span>
              </span>
            </Row>
          )}
          <Row label="Medical certificate">
            {mccdMissing ? (
              <span className="text-amber-700" title="Medical certificate missing">
                {MCCD_STATUS_LABEL.missing}
              </span>
            ) : mccd ? (
              <span className={MCCD_TONE[mccd] ?? ''}>{MCCD_STATUS_LABEL[mccd]}</span>
            ) : (
              <span className="text-muted-foreground text-xs">…</span>
            )}
          </Row>
          <Row label="Pacemaker">
            <span>{pacemakerLabel(record)}</span>
          </Row>
          <BelongingsEdit record={record} />
          <BelongingsShipping record={record} />
          {record.dropoff_notes && (
            <Row label="Notes">
              <span className="whitespace-pre-wrap break-words">{record.dropoff_notes}</span>
            </Row>
          )}
          <Row label="Cremation date">
            {record.cremation_date ? (
              <span>{shortDay(record.cremation_date)}</span>
            ) : (
              <span className="text-muted-foreground text-xs">Not set yet</span>
            )}
          </Row>
          {storageDays > 0 && (
            <Row label="Extra storage">
              <span>
                {storageDays} {storageDays === 1 ? 'day' : 'days'}
              </span>
            </Row>
          )}
          <p className="pt-1 text-xs text-muted-foreground">
            Entered by the facility in its partner portal · read-only here, except the
            belongings.
          </p>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
